import { useQuery, useQueryClient } from "@tanstack/react-query";
import { differenceInCalendarDays } from "date-fns";
import { BellRing, Loader2, Mail, Phone, Check, Grape, Users, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTime, fmtDate, formatApiError } from "@/lib/api";

export default function FollowUpsTab() {
  const qc = useQueryClient();
  const remindersQ = useQuery({
    queryKey: ["admin-reminders"],
    queryFn: () => api.get("/admin/reminders").then((r) => r.data),
  });
  const reminders = remindersQ.data?.reminders || [];

  const markDone = async (id) => {
    try {
      await api.patch(`/admin/reminders/${id}`, { status: "done" });
      await qc.invalidateQueries({ queryKey: ["admin-reminders"] });
      toast.success("Marked done.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-followups-tab" className="max-w-3xl">
      <div className="mb-8">
        <h2 className="font-serif text-3xl text-ink">Annual follow-ups</h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-stone-500">
          When you archive a successful event, we watch its anniversary. A reminder appears here
          45 days before — the perfect moment to reach out about this year's date. Reminders are
          created for recurring celebrations: birthdays, anniversaries, graduations, work gatherings,
          and holiday setups.
        </p>
      </div>
      {remindersQ.isLoading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>}
      {!remindersQ.isLoading && reminders.length === 0 && (
        <div className="card p-12 text-center">
          <BellRing className="mx-auto h-8 w-8 text-stone-300" strokeWidth={1.5} />
          <p className="mt-4 font-serif text-2xl text-ink">No follow-ups due</p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-stone-500">
            Archive events from the Inquiries tab once their date has passed — their anniversaries will show up here.
          </p>
        </div>
      )}
      <div className="space-y-3">
        {reminders.map((r) => {
          const daysLeft = differenceInCalendarDays(new Date(r.anniversary_date + "T12:00:00"), new Date());
          return (
            <div key={r.reminder_id} className="card p-5 sm:p-6" data-testid={`reminder-${r.reminder_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="flex flex-wrap items-center gap-2 font-serif text-xl text-ink">
                    {r.name}
                    <span className={`rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${r.kind === "charcuterie" ? "bg-gold/15 text-amber-900" : "bg-stone-200/80 text-stone-600"}`}>
                      {r.kind === "charcuterie" ? <><Grape className="mr-0.5 inline h-3 w-3" />Charcuterie</> : "Venue"}
                    </span>
                  </p>
                  <p className="mt-1 text-xs font-semibold text-amber-700">
                    {daysLeft <= 0 ? "Anniversary is here" : `${daysLeft} days until the anniversary`} — {fmtDate(r.anniversary_date)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a href={`mailto:${r.email}?subject=Another ${r.event_type || "event"} with The Owl's Nest?`}
                    data-testid={`reminder-email-${r.reminder_id}`}
                    className="flex items-center gap-2 rounded-full border border-black/10 px-3.5 py-2 text-xs font-semibold text-stone-600 transition-colors hover:border-black/30 hover:text-ink">
                    <Mail className="h-3.5 w-3.5" /> {r.email}
                  </a>
                  <a href={`tel:${(r.phone || "").replace(/[^+\d]/g, "")}`} data-testid={`reminder-call-${r.reminder_id}`}
                    className="flex items-center gap-2 rounded-full border border-black/10 px-3.5 py-2 text-xs font-semibold text-stone-600 transition-colors hover:border-black/30 hover:text-ink">
                    <Phone className="h-3.5 w-3.5" /> {r.phone}
                  </a>
                  <button onClick={() => markDone(r.reminder_id)} data-testid={`reminder-done-${r.reminder_id}`}
                    className="btn-dark !px-4 !py-2 text-xs">
                    <Check className="h-4 w-4" /> Done
                  </button>
                </div>
              </div>
              <div className="mt-4 rounded-xl bg-bone2 p-4" data-testid={`reminder-info-${r.reminder_id}`}>
                <p className="label !mb-2">Last time — {fmtDate(r.original_date)}</p>
                <p className="text-xs leading-relaxed text-stone-700">
                  {r.event_type}{r.event_type_other ? ` (${r.event_type_other})` : ""}{r.holiday ? ` · ${r.holiday}` : ""}
                </p>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-[11px] text-stone-500">
                  {r.guest_count ? <span className="flex items-center gap-1.5" data-testid={`reminder-guests-${r.reminder_id}`}><Users className="h-3 w-3" />{r.guest_count} guests at their last party</span> : null}
                  {r.location ? <span className="flex items-center gap-1.5" data-testid={`reminder-address-${r.reminder_id}`}><MapPin className="h-3 w-3" />{r.location}</span> : null}
                  {r.charcuterie_style ? <span className="flex items-center gap-1.5"><Grape className="h-3 w-3" />{r.charcuterie_style}</span> : null}
                  {r.services?.length > 0 && r.kind !== "charcuterie" ? <span className="flex items-center gap-1.5"><Grape className="h-3 w-3" />{r.services.join(", ")}</span> : null}
                </div>
                {r.details && (
                  <div className="mt-3 border-t border-black/5 pt-2.5" data-testid={`reminder-instructions-${r.reminder_id}`}>
                    <p className="label !mb-1">Special instructions</p>
                    <p className="whitespace-pre-wrap text-[11px] italic leading-relaxed text-stone-500">&ldquo;{r.details}&rdquo;</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-8 text-[11px] text-stone-400">
        Reminders generate automatically every morning and the moment you archive an event.
      </p>
    </div>
  );
}
